import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap, tap, shareReplay } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';
import { Router } from '@angular/router';
import { ONBOARDING_ROUTES } from 'src/app/shared/enums/appRoutes.enum';
import { API_URL } from '../../../assets/http/apis';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { IAuthResponse } from '../interfaces';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private translateService: TranslateService | null = null;

  /** Single-flight gate so concurrent 401s share one /auth/refresh call. */
  private refresh$: Observable<IAuthResponse> | null = null;

  constructor(
    private authService: AuthService,
    private tokenStorage: TokenStorageService,
    private router: Router,
    private messageService: MessageService,
    private injector: Injector
  ) {}

  /**
   * @description Lazy-loads TranslateService to avoid circular dependency
   * @returns {TranslateService} The TranslateService instance
   */
  private getTranslateService(): TranslateService {
    if (!this.translateService) {
      this.translateService = this.injector.get(TranslateService);
    }
    return this.translateService;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip authentication for static assets and public endpoints
    if (this.isStaticAsset(req.url) || this.isPublicEndpoint(req.url)) {
      return next.handle(req);
    }

    return next.handle(this.attachToken(req)).pipe(
      tap((event: HttpEvent<unknown>) => {
        // On successful authenticated requests, set auth state to true
        if (event instanceof HttpResponse && event.status === 200) {
          // Only set authenticated for protected endpoints (not public or static ones)
          if (!this.isPublicEndpoint(req.url)) {
            this.authService.setIsAuthenticated(true);
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const isAuthCheck = req.headers.has('X-Auth-Check');

        if (error.status === 401) {
          // Don't handle 401s for static assets (translation files, etc.)
          if (this.isStaticAsset(req.url)) {
            return throwError(() => error);
          }

          // The /auth/refresh call itself returning 401 means our refresh token is dead —
          // bail straight to the logout path; do NOT recurse into another refresh attempt.
          if (req.url.includes(API_URL.AUTH.REFRESH)) {
            return this.handleAuthFailure(isAuthCheck);
          }

          // Only attempt refresh when we actually hold a refresh token. Without one
          // (e.g. unauthenticated requests with a stale access token), fall straight
          // through to the existing logout-on-401 path.
          if (!this.tokenStorage.getRefreshToken()) {
            return this.handleAuthFailure(isAuthCheck);
          }

          return this.runRefresh().pipe(
            switchMap(() => next.handle(this.attachToken(req))),
            catchError(() => this.handleAuthFailure(isAuthCheck))
          );
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * @description Attach the current Bearer token to the request, if any.
   */
  private attachToken(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.tokenStorage.getToken();
    if (!token) return req;
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * @description Single-flight refresh. Concurrent 401s subscribe to the same
   * underlying call; the gate clears once the call settles so subsequent
   * 401s can trigger a fresh rotation.
   */
  private runRefresh(): Observable<IAuthResponse> {
    if (!this.refresh$) {
      this.refresh$ = this.authService.refresh().pipe(
        tap({
          next: () => (this.refresh$ = null),
          error: () => (this.refresh$ = null),
        }),
        shareReplay(1)
      );
    }
    return this.refresh$;
  }

  /**
   * @description Replicates the legacy logout-on-401 behaviour: clear tokens,
   * surface the "session expired" toast (only when the user was authenticated,
   * to avoid noise during onboarding) and redirect to login.
   */
  private handleAuthFailure(isAuthCheck: boolean): Observable<never> {
    const wasAuthenticated = this.authService.getIsAuthenticated() === true;

    this.authService.setIsAuthenticated(false);
    this.tokenStorage.clearAll();

    if (!isAuthCheck && wasAuthenticated) {
      this.messageService.add({
        severity: 'error',
        detail: this.getTranslateService().instant('shared.toast.error.session_expired'),
        life: 3000,
      });
      this.router.navigate([ONBOARDING_ROUTES.LOGIN]);
    }
    return EMPTY;
  }

  /**
   * @description Checks if the endpoint is public.
   * @param {string} url - The URL to check.
   * @returns {boolean} - True if the endpoint is public, false otherwise.
   */
  private isPublicEndpoint(url: string): boolean {
    // Treat all static assets and translation files as public
    if (this.isStaticAsset(url)) return true;

    // Get all onboarding-related endpoints from API_URL
    const onboardingEndpoints = [
      API_URL.ONBOARDING.EMAIL_VALIDATION,
      API_URL.ONBOARDING.SEND_MAGIC_LINK,
      API_URL.ONBOARDING.VERIFY_TOKEN,
      API_URL.ONBOARDING.VALIDATE_USER_INFORMATION,
      API_URL.ONBOARDING.SEND_CODE,
      API_URL.ONBOARDING.VERIFY_CODE,
      API_URL.ONBOARDING.RESEND_CODE,
      API_URL.ONBOARDING.CREATE_USER,
      API_URL.ONBOARDING.COUNTRIES,
      API_URL.ONBOARDING.SPECIALTIES,
      API_URL.AUTH.LOGIN,
      API_URL.AUTH.REFRESH,
      API_URL.AUTH.FORGOT_PASSWORD,
      API_URL.AUTH.RESET_PASSWORD,
    ];
    return onboardingEndpoints.some(endpoint => url.includes(endpoint));
  }

  /**
   * @description Checks if the URL is a static asset (translation files, images, etc.)
   * @param {string} url - The URL to check
   * @returns {boolean} - True if it's a static asset
   */
  private isStaticAsset(url: string): boolean {
    return url.includes('/assets/') || url.includes('/i18n/') || url.endsWith('.json') || url.includes('favicon.ico');
  }
}
